import { useId, useState } from 'react';
import type { ReactNode } from 'react';

import { Button, Checkbox, Dialog, Field, Input, Select, Textarea } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { loiTheoConTro } from '../../vai-tro/nghiep-vu/vai-tro';
import { NHAN_KIEU_DU_LIEU } from '../constants';
import { kiemKhoa, tinhPatch } from '../nghiep-vu/loai-chung-tu';
import { FIELD_DATA_TYPES } from '../types';
import type { FieldDataType, OcrColumn, OcrColumnInput, OcrTable, OcrTableInput } from '../types';

/** Khung chung của hai hộp: lỗi server trong hộp (hộp không đóng), nút lưu nhắc lại việc. */
function useLoiHop(oTrenForm: RegExp) {
  const [loiServer, setLoiServer] = useState<{ loi: ApiError; truong: Record<string, string> } | null>(null);
  const loiKhac = Object.entries(loiServer?.truong ?? {}).filter(([d]) => !oTrenForm.test(d));
  return {
    loiServer,
    xoa: () => setLoiServer(null),
    ghi: (err: unknown) => setLoiServer({ loi: err as ApiError, truong: loiTheoConTro((err as ApiError).details) }),
    loiKhac,
    coLoiTheoO: Boolean(loiServer && Object.keys(loiServer.truong).length > 0 && loiKhac.length === 0),
  };
}

function KhungHop({
  open,
  tieuDe,
  nhanLuu,
  dangGui,
  formId,
  loi,
  onClose,
  onGui,
  children,
}: {
  open: boolean;
  tieuDe: string;
  nhanLuu: string;
  dangGui: boolean;
  formId: string;
  loi: ReturnType<typeof useLoiHop>;
  onClose: () => void;
  onGui: () => void;
  children: ReactNode;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={tieuDe}
      width={560}
      destroyOnHidden
      footer={
        <div className="space-y-3 text-left">
          {loi.loiServer && (
            <LoiTrongHop>
              {loi.coLoiTheoO ? t('cauHinhOcr.loiTheoO') : dichLoi(loi.loiServer.loi)}
              {loi.loiKhac.map(([d, c]) => (
                <span key={d} className="mt-1 block">
                  <span className="font-mono">{d}</span>: {c}
                </span>
              ))}
            </LoiTrongHop>
          )}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={dangGui}>
              {dangGui ? t('chung.dangLuu') : nhanLuu}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          onGui();
        }}
      >
        {children}
      </form>
    </Dialog>
  );
}

type BanBang = { table_key: string; name: string; description: string; has_header_row: boolean };

/** Hộp thêm / sửa một bảng. Thêm thì gửi `columns: []`; cột thêm sau ở thẻ của bảng. */
export function HopBang({
  open,
  bang,
  dangGui,
  onClose,
  onLuu,
}: {
  open: boolean;
  bang: OcrTable | null;
  dangGui: boolean;
  onClose: () => void;
  onLuu: (du: Partial<OcrTableInput>) => Promise<unknown>;
}) {
  const { t } = useT();
  const formId = useId();
  const goc: BanBang = {
    table_key: bang?.table_key ?? '',
    name: bang?.name ?? '',
    description: bang?.description ?? '',
    has_header_row: bang?.has_header_row ?? true,
  };
  const [ban, setBan] = useState<BanBang>(goc);
  const [daGui, setDaGui] = useState(false);
  const loi = useLoiHop(/^\/(table_key|name|description|has_header_row)$/);
  const doi = <K extends keyof BanBang>(k: K, v: BanBang[K]) => {
    setBan((b) => ({ ...b, [k]: v }));
    loi.xoa();
  };
  const maKhoa = kiemKhoa(ban.table_key);

  const gui = async () => {
    setDaGui(true);
    if (maKhoa || !ban.name.trim() || dangGui) return;
    const du = tinhPatch<BanBang>(bang ? goc : {}, { ...ban, table_key: ban.table_key.trim() }, ['description']);
    if (bang && Object.keys(du).length === 0) return onClose();
    loi.xoa();
    try {
      await onLuu(bang ? du : { ...du, columns: [] });
    } catch (err) {
      loi.ghi(err);
    }
  };

  return (
    <KhungHop
      open={open}
      tieuDe={t(bang ? 'cauHinhOcr.bangSuaTieuDe' : 'cauHinhOcr.bangThemTieuDe')}
      nhanLuu={t(bang ? 'cauHinhOcr.luuBang' : 'cauHinhOcr.themBang')}
      dangGui={dangGui}
      formId={formId}
      loi={loi}
      onClose={onClose}
      onGui={() => void gui()}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={t('cauHinhOcr.fKhoa')}
          required
          hint={bang ? t('cauHinhOcr.fKhoaBangGoiY') : t('cauHinhOcr.fKhoaGoiY')}
          error={
            (daGui && maKhoa ? t(maKhoa === 'trong' ? 'cauHinhOcr.fKhoaTrong' : 'cauHinhOcr.fKhoaSai') : undefined) ??
            loi.loiServer?.truong['/table_key']
          }
        >
          {(p) => (
            <Input
              {...p}
              autoFocus={!bang}
              autoComplete="off"
              className="font-mono"
              maxLength={100}
              placeholder="hang_hoa"
              value={ban.table_key}
              onChange={(e) => doi('table_key', e.target.value)}
            />
          )}
        </Field>
        <Field
          label={t('cauHinhOcr.fTenBang')}
          required
          error={(daGui && !ban.name.trim() ? t('cauHinhOcr.fTenBangTrong') : undefined) ?? loi.loiServer?.truong['/name']}
        >
          {(p) => <Input {...p} maxLength={255} value={ban.name} onChange={(e) => doi('name', e.target.value)} />}
        </Field>
      </div>
      <Field label={t('cauHinhOcr.fMoTaBang')} error={loi.loiServer?.truong['/description']}>
        {(p) => (
          <Textarea {...p} rows={2} maxLength={1000} value={ban.description} onChange={(e) => doi('description', e.target.value)} />
        )}
      </Field>
      <Checkbox checked={ban.has_header_row} onChange={(e) => doi('has_header_row', e.target.checked)}>
        <span className="text-sm text-content-secondary">{t('cauHinhOcr.fCoTieuDe')}</span>
      </Checkbox>
      <p className="text-xs text-content-muted">{t('cauHinhOcr.fCoTieuDeMo')}</p>
    </KhungHop>
  );
}

type BanCot = {
  column_key: string;
  label: string;
  data_type: FieldDataType;
  is_required: boolean;
  description: string;
  source_label: string;
};

/** Hộp thêm / sửa một cột của bảng. */
export function HopCot({
  open,
  cot,
  tenBang,
  dangGui,
  onClose,
  onLuu,
}: {
  open: boolean;
  cot: OcrColumn | null;
  tenBang: string;
  dangGui: boolean;
  onClose: () => void;
  onLuu: (du: Partial<OcrColumnInput>) => Promise<unknown>;
}) {
  const { t } = useT();
  const formId = useId();
  const goc: BanCot = {
    column_key: cot?.column_key ?? '',
    label: cot?.label ?? '',
    data_type: cot?.data_type ?? 'TEXT',
    is_required: cot?.is_required ?? false,
    description: cot?.description ?? '',
    source_label: cot?.source_label ?? '',
  };
  const [ban, setBan] = useState<BanCot>(goc);
  const [daGui, setDaGui] = useState(false);
  const loi = useLoiHop(/^\/(column_key|label|data_type|is_required|description|source_label)$/);
  const doi = <K extends keyof BanCot>(k: K, v: BanCot[K]) => {
    setBan((b) => ({ ...b, [k]: v }));
    loi.xoa();
  };
  const maKhoa = kiemKhoa(ban.column_key);

  const gui = async () => {
    setDaGui(true);
    if (maKhoa || !ban.label.trim() || dangGui) return;
    const du = tinhPatch<BanCot>(cot ? goc : {}, { ...ban, column_key: ban.column_key.trim() }, ['description', 'source_label']);
    if (cot && Object.keys(du).length === 0) return onClose();
    loi.xoa();
    try {
      await onLuu(du as Partial<OcrColumnInput>);
    } catch (err) {
      loi.ghi(err);
    }
  };

  return (
    <KhungHop
      open={open}
      tieuDe={t(cot ? 'cauHinhOcr.cotSuaTieuDe' : 'cauHinhOcr.cotThemTieuDe', { bang: tenBang })}
      nhanLuu={t(cot ? 'cauHinhOcr.luuCot' : 'cauHinhOcr.themCot')}
      dangGui={dangGui}
      formId={formId}
      loi={loi}
      onClose={onClose}
      onGui={() => void gui()}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={t('cauHinhOcr.fKhoa')}
          required
          hint={t('cauHinhOcr.fKhoaGoiY')}
          error={
            (daGui && maKhoa ? t(maKhoa === 'trong' ? 'cauHinhOcr.fKhoaTrong' : 'cauHinhOcr.fKhoaSai') : undefined) ??
            loi.loiServer?.truong['/column_key']
          }
        >
          {(p) => (
            <Input
              {...p}
              autoFocus={!cot}
              autoComplete="off"
              className="font-mono"
              maxLength={100}
              placeholder="don_gia"
              value={ban.column_key}
              onChange={(e) => doi('column_key', e.target.value)}
            />
          )}
        </Field>
        <Field
          label={t('cauHinhOcr.fNhan')}
          required
          error={(daGui && !ban.label.trim() ? t('cauHinhOcr.fNhanTrong') : undefined) ?? loi.loiServer?.truong['/label']}
        >
          {(p) => <Input {...p} maxLength={255} value={ban.label} onChange={(e) => doi('label', e.target.value)} />}
        </Field>
        <Field label={t('cauHinhOcr.fKieu')} required error={loi.loiServer?.truong['/data_type']}>
          {(p) => (
            <Select<FieldDataType>
              {...p}
              className="w-full"
              value={ban.data_type}
              onChange={(v) => doi('data_type', v)}
              options={FIELD_DATA_TYPES.map((v) => ({ value: v, label: t(NHAN_KIEU_DU_LIEU[v]) }))}
            />
          )}
        </Field>
        <Field label={t('cauHinhOcr.fNhanIn')} error={loi.loiServer?.truong['/source_label']}>
          {(p) => (
            <Input {...p} maxLength={255} value={ban.source_label} onChange={(e) => doi('source_label', e.target.value)} />
          )}
        </Field>
      </div>
      <Checkbox checked={ban.is_required} onChange={(e) => doi('is_required', e.target.checked)}>
        <span className="text-sm text-content-secondary">{t('cauHinhOcr.fBatBuoc')}</span>
      </Checkbox>
      <Field label={t('cauHinhOcr.fMoTaTruong')} error={loi.loiServer?.truong['/description']}>
        {(p) => (
          <Textarea {...p} rows={2} maxLength={1000} value={ban.description} onChange={(e) => doi('description', e.target.value)} />
        )}
      </Field>
    </KhungHop>
  );
}
